import { useCallback, useEffect, useRef, useState } from "react";
import { fetchDensityDetail } from "./density-api";
import type { DetailState } from "./density-detail-content";

interface UseDensityDetailOptions {
  /** Открытая плотность, `null` — панель закрыта и запрашивать нечего. */
  densityId: string | null;
  /** Сессия истекла: тот же исход, что у потока, — увести на форму входа. */
  onSessionInvalid: () => void;
}

interface UseDensityDetailResult {
  state: DetailState;
  refresh: () => void;
}

/** Последний осевший результат запроса вместе с плотностью, к которой он относится. */
interface DetailResult {
  densityId: string;
  state: DetailState;
}

/** Одно и то же значение на все кадры «ещё не пришло»: панель под `memo` не должна видеть новый объект. */
const LOADING: DetailState = { status: "loading" };

/**
 * Внешнее состояние хука по осевшему результату и плотности, открытой прямо
 * сейчас. Вынесено чистой функцией: гонка между сменой плотности и опоздавшим
 * ответом ловится сравнением идентификаторов, без ожидания эффекта: пока
 * результат относится к чужой (или уже закрытой) плотности, наружу идёт
 * `loading`, а не устаревшие величины прежней.
 */
export function deriveDetailState(
  result: DetailResult | null,
  densityId: string | null,
): DetailState {
  if (densityId === null || result === null || result.densityId !== densityId) {
    return LOADING;
  }
  return result.state;
}

/**
 * Запрос деталей §7.6 для открытой панели. Величины не обновляются сами:
 * повторный запрос — действие человека, поэтому здесь нет таймера, и его
 * не будет, пока интервал не придёт ключом §11.
 */
export function useDensityDetail({
  densityId,
  onSessionInvalid,
}: UseDensityDetailOptions): UseDensityDetailResult {
  const [result, setResult] = useState<DetailResult | null>(null);
  // Номер запроса: пока идёт первый, человек мог закрыть панель и открыть
  // другую плотность, и опоздавший ответ показал бы чужие величины.
  const requestRef = useRef(0);

  const load = useCallback(
    (id: string) => {
      const request = requestRef.current + 1;
      requestRef.current = request;
      setResult({ densityId: id, state: { status: "loading" } });

      fetchDensityDetail(id).then((response) => {
        if (requestRef.current !== request) {
          return;
        }
        if (response.status === "unauthorized") {
          onSessionInvalid();
          return;
        }
        setResult({
          densityId: id,
          state:
            response.status === "ok"
              ? { status: "ready", detail: response.detail }
              : { status: response.status },
        });
      });
    },
    [onSessionInvalid],
  );

  useEffect(() => {
    if (densityId === null) {
      // Панель закрыта: опоздавший ответ по ней в состояние садиться не
      // должен, поэтому текущий запрос инвалидируется, а не просто перестаёт
      // показываться.
      requestRef.current += 1;
      setResult(null);
      return;
    }
    load(densityId);
  }, [densityId, load]);

  const refresh = useCallback(() => {
    if (densityId !== null) {
      load(densityId);
    }
  }, [densityId, load]);

  return { state: deriveDetailState(result, densityId), refresh };
}
